import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { MISSING_DOCS_MODAL_SEEN_KEY } from '../../Utils/missingSettlementDocsModal';
import { getUserDetails, updateUser } from '../../Module/User/Presentation/userActions';
import { 
  selectUserProfile, 
  selectUserStatus, 
  selectUserUpdateStatus, 
  selectUserUpdateError 
} from '../../Module/User/Presentation/userSelectors';
import { clearUpdateStatus } from '../../Module/User/Presentation/userSlice';
import type { UserProfile, UpdateUserRequest } from '../../Module/User/Domain/entity/UserTypes';
import { isIndividualUserType, isOrganizationDropdownForIndividual, isOrganizationRequiredForIndividual, normalizeUserType } from '../../Utils/userType';
import { getOrganizationCode, isOrganizationInPredefinedList, ORGANIZATION_ETC_CODE } from '../../Utils/organizationOptions';
import { formatUserFullName } from '../../Utils/formatUserName';
import { isAuthenticationComplete } from '../../Utils/authenticationBy';
import {
  isValidKoreanResidentRegistrationNumber,
  normalizeKoreanRrnInput,
} from '../../Utils/koreanResidentRegistrationNumber';

/** 서버 프로필 기준 폼 스냅샷 — initializeFromProfile / isDirty 비교에 공통 사용 */
type AccountFormSnapshot = {
  profileImage: string | null;
  contactName: string;
  organization: string;
  organizationOther: string;
  birthDate: string;
  country: string;
  language: string;
  certificateFile: string | null;
  ministryCountry: string;
  isSecurityCountry: boolean;
  bankCountry: 'KR' | 'US';
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  bankbookFile: string | null;
  usTransferMethod: 'check' | 'zelle';
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  zelleContact: string;
  hasNoUniqueId: boolean;
  uniqueId: string;
  residentRegistrationNumber: string;
  idFile: string | null;
  verificationStatus: 'none' | 'in-progress' | 'complete';
};

/** GET 응답이 snake_case / camelCase 혼용일 때 문자열 필드 통일 */
function profileStr(profile: UserProfile, ...keys: string[]): string {
  const raw = profile as Record<string, unknown>;
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

/** 파일 선택 후 아직 저장되지 않은 data:/blob: 미리보기 — 서버 URL만으로는 완료 패널을 쓴다 */
export function isPendingLocalDocumentFile(file: string | null): boolean {
  if (!file) return false;
  const s = file.trim();
  return s.startsWith('data:') || s.startsWith('blob:');
}

/** 통장 이미지가 서버에 있고, 로컬에서 아직 교체용 파일을 고르지 않은 상태 */
export function isBankbookMarkedUploadedOnProfile(
  profile: UserProfile | undefined,
  bankbookFile: string | null,
): boolean {
  if (!profile) return false;
  const saved = profileStr(profile, 'bankbook_image_url', 'bankbookImageUrl').length > 0;
  return saved && !isPendingLocalDocumentFile(bankbookFile);
}

/** 소속 증명서 이미지가 서버에 있고, 교체용 로컬 파일이 없음 */
export function isAuthenticationImageMarkedUploadedOnProfile(
  profile: UserProfile | undefined,
  certificateFile: string | null,
): boolean {
  if (!profile) return false;
  const saved = profileStr(profile, 'authentication_image_url', 'authenticationImageUrl').length > 0;
  return saved && !isPendingLocalDocumentFile(certificateFile);
}

/** 본인확인 카드 이미지가 서버에 있고, 교체용 로컬 파일이 없음 */
export function isRegistrationCardMarkedUploadedOnProfile(
  profile: UserProfile | undefined,
  idFile: string | null,
): boolean {
  if (!profile) return false;
  const saved = profileStr(profile, 'registration_card_url', 'registrationCardUrl').length > 0;
  return saved && !isPendingLocalDocumentFile(idFile);
}

/** 정산 신분증/고유번호증 사본이 필요한 회원 여부 — 탭 노랑 경고 로직과 동일 */
export function profileNeedsRegistrationCard(profile: UserProfile): boolean {
  const isIndividual = isIndividualUserType(profile.user_type);
  const organizationWithoutUniqueId =
    !isIndividual && !String(profile.business_registration ?? '').trim();
  return isIndividual || !organizationWithoutUniqueId;
}

/** 서버에 은행/송금 정보(한국: 은행·계좌·예금주, 미국: 주소 또는 Zelle)가 저장된 뒤 해당 필드 수정 불가 */
function isBankProfileLockedOnServer(profile: UserProfile | undefined): boolean {
  if (!profile) return false;
  const isUs = String(profile.bank_location_country_code ?? 'KR').toUpperCase() === 'US';
  if (isUs) {
    const zelleContact = String(profile.paypal_email ?? '').trim();
    if (zelleContact.length > 0) return true;
    return (
      String(profile.bank_account_holder ?? '').trim().length > 0 &&
      String(profile.address1 ?? '').trim().length > 0 &&
      String(profile.city ?? '').trim().length > 0 &&
      String(profile.state_province ?? '').trim().length > 0 &&
      String(profile.zipcode ?? '').trim().length > 0
    );
  }
  return (
    String(profile.bank_name ?? '').trim().length > 0 &&
    String(profile.bank_account ?? '').trim().length > 0 &&
    String(profile.bank_account_holder ?? '').trim().length > 0
  );
}

/** 한국: 은행명·계좌·예금주가 서버에 저장되어 있음 (통장 이미지는 별도) */
export function hasKoreanBankCoreOnServer(profile: UserProfile | undefined): boolean {
  if (!profile) return false;
  const code = String(profile.bank_location_country_code ?? 'KR').toUpperCase();
  if (code === 'US') return false;
  return (
    String(profile.bank_name ?? '').trim().length > 0 &&
    String(profile.bank_account ?? '').trim().length > 0 &&
    String(profile.bank_account_holder ?? '').trim().length > 0
  );
}

function deriveAccountFormSnapshot(
  profile: UserProfile,
  getLanguageLabel: (value?: string) => string,
): AccountFormSnapshot {
  const orgTrimmed = (profile.organization || '').trim();
  let computedName = formatUserFullName(profile.first_name, profile.last_name).trim();
  const isOrgUser = !isIndividualUserType(profile.user_type);
  if (isOrgUser && orgTrimmed && (computedName === orgTrimmed || profile.first_name === orgTrimmed || profile.last_name === orgTrimmed)) {
    computedName = '';
  }
  const orgFromProfile = (profile.organization || '').trim();
  const isDropdownOrgType = isIndividualUserType(profile.user_type) && isOrganizationDropdownForIndividual(profile.user_type);
  let organization = '';
  let organizationOther = '';
  if (!orgFromProfile) {
    organization = '';
    organizationOther = '';
  } else if (isDropdownOrgType) {
    const isInList = isOrganizationInPredefinedList(orgFromProfile);
    if (isInList) {
      organization = getOrganizationCode(orgFromProfile);
      organizationOther = '';
    } else {
      organization = ORGANIZATION_ETC_CODE;
      organizationOther = orgFromProfile;
    }
  } else {
    organization = orgFromProfile;
    organizationOther = '';
  }

  const authImageUrl = profileStr(profile, 'authentication_image_url', 'authenticationImageUrl');
  const regCardUrl = profileStr(profile, 'registration_card_url', 'registrationCardUrl');
  const isIndividualUser = isIndividualUserType(profile.user_type);
  const bankLocationCode = profile.bank_location_country_code || 'KR';
  const isUs = String(bankLocationCode).toUpperCase() === 'US';
  let hasNoUniqueId = false;
  let uniqueId = '';
  let residentRegistrationNumber = '';
  if (isIndividualUser) {
    residentRegistrationNumber = normalizeKoreanRrnInput(profile.business_registration || '');
    hasNoUniqueId = false;
    uniqueId = '';
  } else {
    // 단체 기본값: "고유번호증이 있습니다." 체크된 상태
    hasNoUniqueId = false;
    uniqueId = profile.business_registration || '';
    residentRegistrationNumber = '';
  }

  // 개인/단체: 심사 완료(authentication_by)면 complete.
  // 제출·심사 중(in-progress): 본인확인 서류 URL, 단체 증빙, 미국 계좌 확정, 또는 한국 계좌 정보(은행·계좌·예금주)까지 서버 저장된 경우
  //  ※ 한국 통장·신분증 첨부는 선택이어도 계좌 코어만 저장되면 스텝 모드를 종료한다.
  let verificationStatus: AccountFormSnapshot['verificationStatus'] = 'none';
  if (isAuthenticationComplete(profile.authentication_by)) {
    verificationStatus = 'complete';
  } else if (regCardUrl || (authImageUrl && !isIndividualUser)) {
    verificationStatus = 'in-progress';
  } else if (isUs && isBankProfileLockedOnServer(profile)) {
    // 미국 계좌는 본인확인 단계를 건너뛰고, 계좌 정보 저장 후 심사 진행 상태로 본다.
    verificationStatus = 'in-progress';
  } else if (!isUs && hasKoreanBankCoreOnServer(profile)) {
    // 한국: 통장/신분증 미첨부여도 계좌 정보 제출 완료 시 스텝 모드 종료(심사 중 UI)
    verificationStatus = 'in-progress';
  }
  return {
    profileImage: profile.image_url || profile.sns_thumbnail || null,
    contactName: computedName,
    organization,
    organizationOther,
    /** 표시: 서버 birth_date 우선, 없으면 카카오 등 sns_birth (저장 후에는 보통 birth_date로 확정) */
    birthDate: String(profile.birth_date ?? '').trim() || String(profile.sns_birth ?? '').trim() || '',
    country: profile.country_code || '',
    language: profile.language_type ? getLanguageLabel(profile.language_type) : '',
    certificateFile: authImageUrl || null,
    ministryCountry: profile.mission_field || '',
    isSecurityCountry: profile.restricted_region !== false && String(profile.restricted_region) !== 'false',
    bankCountry: bankLocationCode === 'US' ? 'US' : 'KR',
    bankName: profile.bank_name || '',
    accountNumber: profile.bank_account || '',
    accountHolder: profile.bank_account_holder || '',
    bankbookFile: profileStr(profile, 'bankbook_image_url', 'bankbookImageUrl') || null,
    usTransferMethod: profile.paypal_email ? 'zelle' : 'check',
    streetAddress: profile.address1 || '',
    city: profile.city || '',
    state: profile.state_province || '',
    zipCode: profile.zipcode || '',
    zelleContact: profile.paypal_email || '',
    hasNoUniqueId,
    uniqueId,
    residentRegistrationNumber,
    idFile: regCardUrl || null,
    verificationStatus,
  };
}

/** 개인: 서버에 유효 주민번호가 저장된 뒤 주민번호는 수정 불가(신분증은 별도로 보완). 단체는 항상 수정 가능. */
function isVerificationProfileLockedOnServer(profile: UserProfile | undefined): boolean {
  if (!profile || !isIndividualUserType(profile.user_type)) return false;
  const rrn = normalizeKoreanRrnInput(profile.business_registration || '');
  return rrn.length === 13 && isValidKoreanResidentRegistrationNumber(rrn);
}

const languageCodeToKey = {
  ko: 'common.languages.ko',
  en: 'common.languages.en',
  zh: 'common.languages.zh',
  ja: 'common.languages.ja',
} as const;

type LanguageCode = keyof typeof languageCodeToKey;
type LanguageKey = (typeof languageCodeToKey)[LanguageCode];

const isLanguageCode = (value: string): value is LanguageCode => value in languageCodeToKey;

const languageNameOverrides: Record<string, LanguageCode> = {
  Korean: 'ko',
  English: 'en',
  Chinese: 'zh',
  Japanese: 'ja',
};

const userTypeToKey: Record<string, string> = {
  missionary: 'page.myAccount.userType.missionary',
  pastor: 'page.myAccount.userType.pastor',
  staff: 'page.myAccount.userType.staff',
  private: 'page.myAccount.userType.private',
  ngo: 'page.myAccount.userType.ngo',
  church: 'page.myAccount.userType.church',
  orphanage: 'page.myAccount.userType.orphanage',
  smallgroup: 'page.myAccount.userType.smallGroup',
  missionschool: 'page.myAccount.userType.missionSchool',
  etc: 'page.myAccount.userType.etc',
  organization: 'page.myAccount.userType.organization',
  individual: 'page.myAccount.userType.individual',
  group: 'page.myAccount.userType.group',
};

// user_desc 값(정규화) → 회원가입 시 선택한 세부 모금자 유형 번역 키
const userDescToKey: Record<string, string> = {
  pastor: 'page.signup.step3.categories.churchWorker',
  missionary: 'page.signup.step3.categories.missionary',
  staff: 'page.signup.step3.categories.organizationWorker',
  private: 'page.signup.step3.categories.individualEtc',
  ngo: 'page.signup.step3.categories.nonProfit',
  church: 'page.myAccount.userType.church',
  orphanage: 'page.signup.step3.categories.orphanage',
  smallgroup: 'page.signup.step3.categories.prison',
  missionschool: 'page.signup.step3.categories.school',
  etc: 'page.signup.step3.categories.groupEtc',

  // legacy values
  church_worker: 'page.signup.step3.categories.churchWorker',
  organization_worker: 'page.signup.step3.categories.organizationWorker',
  individual_etc: 'page.signup.step3.categories.individualEtc',
  non_profit: 'page.signup.step3.categories.nonProfit',
  prison: 'page.signup.step3.categories.prison',
  school: 'page.signup.step3.categories.school',
  group_etc: 'page.signup.step3.categories.groupEtc',
};

export type ActiveTab = 'basic' | 'ministry' | 'bank' | 'verification';

const tabOrder: ActiveTab[] = ['basic', 'ministry', 'bank', 'verification'];
const usdBankTabOrder: ActiveTab[] = ['basic', 'ministry', 'bank'];

export interface UseMyAccountReturn {
  // 로딩/에러 상태
  isLoading: boolean;
  isSaving: boolean;
  error?: string;
  saveError?: string;
  userProfile?: UserProfile;
  
  // 탭 상태
  activeTab: ActiveTab;
  setActiveTab: (tab: ActiveTab) => void;
  
  // 기본정보
  profileImage: string | null;
  setProfileImage: (image: string | null) => void;
  userType: string;
  organization: string;
  setOrganization: (value: string) => void;
  organizationOther: string;
  setOrganizationOther: (value: string) => void;
  fullName: string;
  contactName: string;
  setContactName: (value: string) => void;
  birthDate: string;
  setBirthDate: (value: string) => void;
  /** 서버에 birth_date가 한 번이라도 저장된 경우 수정 불가 (프로필 갱신·카카오 연동 반영은 GET으로 동기화) */
  isBirthDateLocked: boolean;
  /** 서버에 은행(또는 미국 정산 주소) 정보가 저장된 경우 수정 불가 */
  isBankInfoLocked: boolean;
  /** 개인만: 서버에 유효 주민번호가 저장된 경우 주민번호 수정 불가 (신분증은 별도로 보완). 단체는 false */
  isVerificationLocked: boolean;
  /** 탭 모드에서 한국 계좌 저장 후 통장 사본 미첨부일 때 결제 정보 탭 옆 노랑 표시 용도 */
  showBankTabSettlementWarning: boolean;
  /** 탭 모드에서 본인확인 서류가 필요하면 확인 서류 탭 옆 노랑 표시 용도 */
  showVerificationTabSettlementWarning: boolean;
  phone: string;
  email: string;
  country: string;
  setCountry: (value: string) => void;
  language: string;
  setLanguage: (value: string) => void;
  
  // 사역정보
  certificateFile: string | null;
  setCertificateFile: (file: string | null) => void;
  ministryCountry: string;
  setMinistryCountry: (value: string) => void;
  isSecurityCountry: boolean;
  setIsSecurityCountry: (value: boolean) => void;
  
  // 은행정보
  bankCountry: 'KR' | 'US';
  setBankCountry: (country: 'KR' | 'US') => void;
  bankName: string;
  setBankName: (value: string) => void;
  accountNumber: string;
  setAccountNumber: (value: string) => void;
  accountHolder: string;
  setAccountHolder: (value: string) => void;
  bankbookFile: string | null;
  setBankbookFile: (file: string | null) => void;
  usTransferMethod: 'check' | 'zelle';
  setUsTransferMethod: (method: 'check' | 'zelle') => void;
  
  // 미국 주소
  streetAddress: string;
  setStreetAddress: (value: string) => void;
  city: string;
  setCity: (value: string) => void;
  state: string;
  setState: (value: string) => void;
  zipCode: string;
  setZipCode: (value: string) => void;
  zelleContact: string;
  setZelleContact: (value: string) => void;
  
  // 본인 확인
  hasNoUniqueId: boolean;
  setHasNoUniqueId: (value: boolean) => void;
  uniqueId: string;
  setUniqueId: (value: string) => void;
  residentRegistrationNumber: string;
  setResidentRegistrationNumber: (value: string) => void;
  idFile: string | null;
  setIdFile: (file: string | null) => void;
  
  // 검증 상태
  verificationStatus: 'none' | 'in-progress' | 'complete';
  setVerificationStatus: (status: 'none' | 'in-progress' | 'complete') => void;
  
  // 에러 처리
  errors: Record<string, boolean>;
  setErrors: (errors: Record<string, boolean>) => void;
  showErrors: boolean;
  setShowErrors: (show: boolean) => void;
  
  // 모달
  isSubmitModalOpen: boolean;
  setIsSubmitModalOpen: (open: boolean) => void;

  /** 탭 모드에서 통장+신분증 누락을 채워 저장까지 마친 뒤 정산 서류 제출 완료 안내 */
  isSettlementDocsCompleteModalOpen: boolean;
  setIsSettlementDocsCompleteModalOpen: (open: boolean) => void;

  /** 저장된 프로필 대비 폼에 수정이 있으면 true (취소 확인 모달용) */
  isDirty: boolean;

  // 액션
  validate: () => boolean;
  handleSave: () => void;
  handleCancel: () => void;
  handleNext: () => void;
  handlePrev: () => void;
  handleClearAll: () => void;
  refetchUser: () => void;
}

export const useMyAccount = (userId?: string): UseMyAccountReturn => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('translation');
  const [searchParams, setSearchParams] = useSearchParams();
  const userProfile = useAppSelector(selectUserProfile);
  const userStatus = useAppSelector(selectUserStatus);
  const updateStatus = useAppSelector(selectUserUpdateStatus);
  const updateError = useAppSelector(selectUserUpdateError);
  
  const isLoading = userStatus === 'loading';
  const isSaving = updateStatus === 'saving';


  const getLanguageLabel = useCallback(
    (value?: string) => {
      if (!value) return '';
      const normalized = languageNameOverrides[value] ?? value;
      if (isLanguageCode(normalized)) return t(languageCodeToKey[normalized]);
      return value;
    },
    [t]
  );

  const getLanguageCode = useCallback(
    (label?: string) => {
      if (!label) return '';
      const override = languageNameOverrides[label];
      if (override) return override;
      const entry = (Object.entries(languageCodeToKey) as [LanguageCode, LanguageKey][])
        .find(([, key]) => t(key) === label);
      return entry?.[0] ?? label;
    },
    [t]
  );

  const getUserTypeLabel = useCallback(
    (type?: string, desc?: string) => {
      const normalized = normalizeUserType(type);
      if (normalized && userTypeToKey[normalized]) return t(userTypeToKey[normalized] as any);
      // user_desc에 세부 유형이 있으면 우선 표시 (회원가입 시 선택한 모금자 유형)
      const normalizedDesc = normalizeUserType(desc);
      if (normalizedDesc && userDescToKey[normalizedDesc]) return t(userDescToKey[normalizedDesc] as any);
      return type ?? '';
    },
    [t]
  );
  
  // 탭 상태
  const [activeTab, setActiveTab] = useState<ActiveTab>('basic');

  useEffect(() => {
    const raw = searchParams.get('tab');
    if (!raw) return;
    const allowed: ActiveTab[] = ['basic', 'ministry', 'bank', 'verification'];
    if (!allowed.includes(raw as ActiveTab)) return;
    setActiveTab(raw as ActiveTab);
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // 기본정보 상태
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [contactName, setContactName] = useState('');
  const [organization, setOrganization] = useState('');
  const [organizationOther, setOrganizationOther] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [country, setCountry] = useState('');
  const [language, setLanguage] = useState('');
  
  // 사역정보 상태
  const [certificateFile, setCertificateFile] = useState<string | null>(null);
  const [ministryCountry, setMinistryCountry] = useState('');
  const [isSecurityCountry, setIsSecurityCountry] = useState(true);
  
  // 은행정보 상태
  const [bankCountry, setBankCountry] = useState<'KR' | 'US'>('KR');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [bankbookFile, setBankbookFile] = useState<string | null>(null);
  const [usTransferMethod, setUsTransferMethod] = useState<'check' | 'zelle'>('check');
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [zelleContact, setZelleContact] = useState('');
  
  // 본인 확인 상태
  const [hasNoUniqueId, setHasNoUniqueId] = useState(false);
  const [uniqueId, setUniqueId] = useState('');
  const [residentRegistrationNumber, setResidentRegistrationNumberRaw] = useState('');
  const setResidentRegistrationNumber = useCallback((value: string) => {
    setResidentRegistrationNumberRaw(normalizeKoreanRrnInput(value));
  }, []);
  const [idFile, setIdFile] = useState<string | null>(null);
  
  // 기타 상태
  const [verificationStatus, setVerificationStatus] = useState<'none' | 'in-progress' | 'complete'>('none');
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isSettlementDocsCompleteModalOpen, setIsSettlementDocsCompleteModalOpen] = useState(false);
  const isUsdBankStepFlow = verificationStatus === 'none' && bankCountry === 'US';
  
  // API에서 가져온 읽기 전용 데이터 (user_desc가 있으면 세부 모금자 유형 표시)
  const userType = userProfile?.user_type
    ? getUserTypeLabel(userProfile.user_type, userProfile.user_desc)
    : '';
    
  const fullName = userProfile
    ? (formatUserFullName(userProfile.first_name, userProfile.last_name) || userProfile.email || '').trim()
    : '';
    
  const phone = userProfile?.phone || '';
  const email = userProfile?.email || '';

  const isBirthDateLocked = useMemo(
    () => Boolean(userProfile && String(userProfile.birth_date ?? '').trim().length > 0),
    [userProfile],
  );

  const isBankInfoLocked = useMemo(() => isBankProfileLockedOnServer(userProfile), [userProfile]);

  const isVerificationLocked = useMemo(
    () => isVerificationProfileLockedOnServer(userProfile),
    [userProfile],
  );

  /** 한국 계좌 제출 후(탭 모드) 통장 사본 미첨부 — 탭 노란 표시 등 */
  const showBankTabSettlementWarning = useMemo(() => {
    if (verificationStatus === 'none' || !userProfile) return false;
    if (!hasKoreanBankCoreOnServer(userProfile)) return false;
    return profileStr(userProfile, 'bankbook_image_url', 'bankbookImageUrl').length === 0;
  }, [userProfile, verificationStatus]);

  /** 정산용 신분증(또는 단체 증빙) 필요 시 본인확인 탭 노란 표시 — 레이아웃 정산 모달 조건과 맞춤 */
  const showVerificationTabSettlementWarning = useMemo(() => {
    if (verificationStatus === 'none' || !userProfile) return false;
    const isKorea = userProfile.bank_location_country_code === 'KR' || !userProfile.bank_location_country_code;
    const hasBankInfo = Boolean(userProfile.bank_account || userProfile.bank_name);
    if (!isKorea || !hasBankInfo) return false;
    if (profileStr(userProfile, 'registration_card_url', 'registrationCardUrl')) return false;
    const isIndividualUser = isIndividualUserType(userProfile.user_type);
    const isOrganizationWithoutUniqueId =
      !isIndividualUser && !String(userProfile.business_registration ?? '').trim();
    const needsId = isIndividualUser || !isOrganizationWithoutUniqueId;
    return needsId;
  }, [userProfile, verificationStatus]);

  // 사용자 정보 초기화
  const initializeFromProfile = useCallback(
    (profile: UserProfile) => {
      const s = deriveAccountFormSnapshot(profile, getLanguageLabel);
      setProfileImage(s.profileImage);
      setContactName(s.contactName);
      setOrganization(s.organization);
      setOrganizationOther(s.organizationOther);
      setBirthDate(s.birthDate);
      setCountry(s.country);
      setLanguage(s.language);
      setCertificateFile(s.certificateFile);
      setMinistryCountry(s.ministryCountry);
      setIsSecurityCountry(s.isSecurityCountry);
      setBankCountry(s.bankCountry);
      setBankName(s.bankName);
      setAccountNumber(s.accountNumber);
      setAccountHolder(s.accountHolder);
      setBankbookFile(s.bankbookFile);
      setUsTransferMethod(s.usTransferMethod);
      setStreetAddress(s.streetAddress);
      setCity(s.city);
      setState(s.state);
      setZipCode(s.zipCode);
      setZelleContact(s.zelleContact);
      setHasNoUniqueId(s.hasNoUniqueId);
      setUniqueId(s.uniqueId);
      setResidentRegistrationNumberRaw(s.residentRegistrationNumber);
      setIdFile(s.idFile);
      setVerificationStatus(s.verificationStatus);
    },
    [getLanguageLabel],
  );
  
  // 프로필 데이터가 변경되면 상태 초기화
  useEffect(() => {
    if (userProfile) {
      initializeFromProfile(userProfile);
    }
  }, [userProfile, initializeFromProfile]);

  const isDirty = useMemo(() => {
    if (!userProfile) return false;
    const expected = deriveAccountFormSnapshot(userProfile, getLanguageLabel);
    const current: AccountFormSnapshot = {
      profileImage,
      contactName,
      organization,
      organizationOther,
      birthDate,
      country,
      language,
      certificateFile,
      ministryCountry,
      isSecurityCountry,
      bankCountry,
      bankName,
      accountNumber,
      accountHolder,
      bankbookFile,
      usTransferMethod,
      streetAddress,
      city,
      state,
      zipCode,
      zelleContact,
      hasNoUniqueId,
      uniqueId,
      residentRegistrationNumber,
      idFile,
      verificationStatus,
    };
    return JSON.stringify(expected) !== JSON.stringify(current);
  }, [
    userProfile,
    getLanguageLabel,
    profileImage,
    contactName,
    organization,
    organizationOther,
    birthDate,
    country,
    language,
    certificateFile,
    ministryCountry,
    isSecurityCountry,
    bankCountry,
    bankName,
    accountNumber,
    accountHolder,
    bankbookFile,
    usTransferMethod,
    streetAddress,
    city,
    state,
    zipCode,
    zelleContact,
    hasNoUniqueId,
    uniqueId,
    residentRegistrationNumber,
    idFile,
    verificationStatus,
  ]);

  // 사용자 데이터 다시 불러오기
  const refetchUser = useCallback(() => {
    if (userId) {
      dispatch(getUserDetails(userId));
    }
  }, [dispatch, userId]);
  
  // 컴포넌트 마운트 시 사용자 데이터 항상 최신으로 로딩
  useEffect(() => {
    if (userId) {
      dispatch(getUserDetails(userId));
    }
  }, [dispatch, userId]);

  /** 다른 송금/은행 선택에서 남은 검증 플래그 제거 — 스텝 모드 등에서 표시가 꼬이지 않도록 */
  useEffect(() => {
    setErrors((prev) => {
      const next = { ...prev };
      let changed = false;
      const removeKeys = (keys: string[]) => {
        keys.forEach((k) => {
          if (k in next) {
            changed = true;
            delete next[k];
          }
        });
      };

      if (bankCountry === 'KR') {
        removeKeys(['zelleContact', 'streetAddress', 'city', 'state', 'zipCode']);
      } else {
        removeKeys(['bankName', 'accountNumber', 'hasBankbookCopy']);
        if (usTransferMethod === 'zelle') {
          removeKeys(['accountHolder', 'streetAddress', 'city', 'state', 'zipCode']);
        } else {
          removeKeys(['zelleContact']);
        }
      }

      return changed ? next : prev;
    });
  }, [bankCountry, usTransferMethod]);
  
  // 유효성 검사
  const validate = useCallback(() => {
    const newErrors: Record<string, boolean> = {};
    
    if (activeTab === 'basic') {
      const isIndividual = isIndividualUserType(userProfile?.user_type);
      if (!isIndividual && !contactName) newErrors.contactName = true;
      // 단체 회원: 소속 단체명 필수 / 개인(교회 사역자, 선교사, 선교단체 사역자): 필수 / 개인 기타: 선택
      if (!isIndividual && !organization) newErrors.organization = true;
      if (isIndividual && isOrganizationRequiredForIndividual(userProfile?.user_type)) {
        if (!organization) newErrors.organization = true;
        if (organization === ORGANIZATION_ETC_CODE && !organizationOther.trim()) newErrors.organizationOther = true;
      }
      if (!birthDate) newErrors.birthDate = true;
      if (!country) newErrors.country = true;
      if (!language) newErrors.language = true;
    } else if (activeTab === 'ministry') {
      if (!ministryCountry) newErrors.ministryCountry = true;
    } else if (activeTab === 'bank') {
      if (!isBankInfoLocked) {
        if (bankCountry === 'KR') {
          if (!bankName) newErrors.bankName = true;
          if (!accountNumber) newErrors.accountNumber = true;
          if (!accountHolder) newErrors.accountHolder = true;
        } else if (usTransferMethod === 'zelle') {
          if (!zelleContact.trim()) newErrors.zelleContact = true;
        } else {
          if (!accountHolder) newErrors.accountHolder = true;
          if (!streetAddress) newErrors.streetAddress = true;
          if (!city) newErrors.city = true;
          if (!state) newErrors.state = true;
          if (!zipCode) newErrors.zipCode = true;
        }
      }
    } else if (activeTab === 'verification' && !isVerificationLocked) {
      const isIndividual = isIndividualUserType(userProfile?.user_type);
      if (isIndividual) {
        const rrnDigits = normalizeKoreanRrnInput(residentRegistrationNumber);
        if (!rrnDigits) {
          newErrors.residentRegistration = true;
        } else if (!isValidKoreanResidentRegistrationNumber(rrnDigits)) {
          newErrors.residentRegistrationInvalid = true;
        }
      } else {
        if (!hasNoUniqueId) {
          if (!uniqueId) newErrors.uniqueId = true;
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [activeTab, contactName, organization, organizationOther, birthDate, country, language, ministryCountry, bankCountry, bankName, accountNumber, accountHolder, usTransferMethod, streetAddress, city, state, zipCode, zelleContact, userProfile, residentRegistrationNumber, hasNoUniqueId, uniqueId, isBankInfoLocked, isVerificationLocked]);
  
  // 저장 요청 데이터 빌드
  const buildUpdateRequest = useCallback((): UpdateUserRequest | null => {
    if (!userId) return null;
    
    // URL vs Base64 Helper
    // - 기존 URL(image_url, sns_thumbnail 등) → image_url 등으로 URL 전송 (재첨부 없이 저장)
    // - 새 업로드(base64) → image 등으로 base64 전송
    const getImageField = (file: string | null) => {
      if (!file) return { url: undefined, base64: undefined };
      if (file.startsWith('http')) return { url: file, base64: undefined };
      const raw = file.includes(',') ? file.split(',')[1] : file;
      return { url: undefined, base64: raw };
    };

    const isIndividual = isIndividualUserType(userProfile?.user_type);

    const profileImg = getImageField(profileImage);
    const certImg = getImageField(certificateFile);
    // 개인: 항상 신분증 전송 / 단체: hasNoUniqueId이면 전송 안함
    const idImg = getImageField(isIndividual ? idFile : (hasNoUniqueId ? null : idFile));
    const bankbookImg = getImageField(bankbookFile);

    const request: UpdateUserRequest = {
      user_id: userId,
      // 기본정보: 단체 회원은 담당자 이름 수정 가능
      first_name: isIndividual ? userProfile?.first_name : '',
      last_name: isIndividual ? userProfile?.last_name : contactName,
      email: userProfile?.email,
      phone: userProfile?.phone,
      user_type: userProfile?.user_type,
      organization: organization === ORGANIZATION_ETC_CODE ? organizationOther.trim() : getOrganizationCode(organization),
      birth_date: birthDate,
      country_code: country,
      language_type: (() => {
        const code = getLanguageCode(language) || language;
        return ['ko', 'en'].includes(code) ? code : 'ko';
      })(),

      // 이미지 필드 (Base64) 및 URL 필드
      image: profileImg.base64,
      image_url: profileImg.url,

      authentication_image: certImg.base64,
      authentication_image_url: certImg.url,

      registration_card_image: isIndividual ? idImg.base64 : (hasNoUniqueId ? '' : idImg.base64),
      registration_card_url: isIndividual ? idImg.url : (hasNoUniqueId ? '' : idImg.url),

      bankbook_image: bankbookImg.base64,
      bankbook_image_url: bankbookImg.url,

      // 사역정보
      mission_field: ministryCountry,
      restricted_region: isSecurityCountry,

      // 은행정보
      bank_location_country_code: bankCountry,
      bank_name: bankCountry === 'US' ? '' : bankName,
      bank_account: bankCountry === 'US' ? '' : accountNumber,
      bank_account_holder: bankCountry === 'US' && usTransferMethod === 'zelle' ? '' : accountHolder,

      // 주소 (미국)
      address1: bankCountry === 'US' && usTransferMethod === 'check' ? streetAddress : '',
      city: bankCountry === 'US' && usTransferMethod === 'check' ? city : '',
      state_province: bankCountry === 'US' && usTransferMethod === 'check' ? state : '',
      zipcode: bankCountry === 'US' && usTransferMethod === 'check' ? zipCode : '',

      // 본인 확인: 개인은 주민등록번호, 단체는 고유번호
      business_registration: isIndividual
        ? residentRegistrationNumber
        : (hasNoUniqueId ? '' : uniqueId),

      // 기타 필수 필드 보존 (누락 방지)
      partner: userProfile?.partner,
      user_desc: userProfile?.user_desc,
      // sns_id 는 로그인 키라서 서버가 SNS 인증을 확인한 경로에서만 저장한다 (여기서 보내지 않는다)
      sns_phone: userProfile?.sns_phone,
      sns_thumbnail: userProfile?.sns_thumbnail,
      sns_name: userProfile?.sns_name,
      sns_birth: userProfile?.sns_birth,
      authentication_by: userProfile?.authentication_by,
      access_at: userProfile?.access_at,
      routing_no: userProfile?.routing_no,
      swift_code: userProfile?.swift_code,
      bank_address: userProfile?.bank_address,
      paypal_email: bankCountry === 'US' && usTransferMethod === 'zelle' ? zelleContact.trim() : '',
    };

    return request;
  }, [
    userId, userProfile, contactName, organization, organizationOther, birthDate, country, language,
    profileImage, ministryCountry, isSecurityCountry, certificateFile,
    bankCountry, bankName, accountNumber, accountHolder, bankbookFile, usTransferMethod,
    streetAddress, city, state, zipCode, zelleContact, hasNoUniqueId, uniqueId, idFile,
    residentRegistrationNumber, getLanguageCode
  ]);
  
  // 저장 핸들러
  const handleSave = useCallback(() => {
    if (!validate()) {
      setShowErrors(true);
      return;
    }

    const rawRequest = buildUpdateRequest();
    if (!rawRequest) return;

    const request = Object.fromEntries(
      Object.entries(rawRequest).filter(([, v]) => v !== undefined)
    ) as UpdateUserRequest;

    dispatch(updateUser(request))
      .unwrap()
      .then(async () => {
        // 저장 성공 시, 정산 서류 등록 안내 모달이 바로 뜨지 않도록 플래그 설정
        if (typeof window !== 'undefined') {
          localStorage.setItem(MISSING_DOCS_MODAL_SEEN_KEY, '1');
        }

        let isSettlementComplete = false;
        if (userId) {
          try {
            const fresh = await dispatch(getUserDetails(userId)).unwrap();
            const hasBankbook = profileStr(fresh, 'bankbook_image_url', 'bankbookImageUrl').length > 0;
            const needsId = profileNeedsRegistrationCard(fresh);
            const hasId = profileStr(fresh, 'registration_card_url', 'registrationCardUrl').length > 0;
            
            isSettlementComplete = hasBankbook && (!needsId || hasId);
          } catch {
            dispatch(getUserDetails(userId));
          }
        }

        const wasStepMode = verificationStatus === 'none';
        if (wasStepMode) {
          setVerificationStatus('in-progress');
        }

        if (bankCountry === 'US') {
          if (activeTab === 'bank' || isUsdBankStepFlow) {
            setIsSubmitModalOpen(true);
          }
        } else {
          const isRelevantTab = activeTab === 'bank' || activeTab === 'verification' || wasStepMode;
          if (isRelevantTab) {
            if (isSettlementComplete) {
              setIsSettlementDocsCompleteModalOpen(true);
            } else if (wasStepMode) {
              // 최초 입력: 서류 미비 시 제출 완료 안내 모달
              setIsSubmitModalOpen(true);
            }
            // 재로그인·탭 모드: 서류가 하나라도 빠지면 모달 없이 저장만
          }
        }
      })
      .catch(() => {
        window.alert(t('page.myAccount.errors.saveUser'));
      });

    setShowErrors(false);
  }, [
    validate,
    buildUpdateRequest,
    dispatch,
    userId,
    userProfile,
    activeTab,
    isUsdBankStepFlow,
    verificationStatus,
    bankCountry,
    t,
  ]);
  
  // 취소 핸들러
  const handleCancel = useCallback(() => {
    setShowErrors(false);
    setErrors({});
    // 프로필 데이터로 상태 리셋
    if (userProfile) {
      initializeFromProfile(userProfile);
    }
  }, [userProfile, initializeFromProfile]);

  // 스텝 모드: 다음 스텝으로 이동 (마지막 스텝이면 저장)
  const handleNext = useCallback(() => {
    const currentTabOrder = bankCountry === 'US' ? usdBankTabOrder : tabOrder;
    const currentIndex = currentTabOrder.indexOf(activeTab);

    if (currentIndex < 0) {
      setActiveTab(currentTabOrder[currentTabOrder.length - 1]);
      setShowErrors(false);
      return;
    }

    if (currentIndex === currentTabOrder.length - 1) {
      // 마지막 스텝 → 저장
      handleSave();
    } else {
      if (validate()) {
        setActiveTab(currentTabOrder[currentIndex + 1]);
        setShowErrors(false);
      } else {
        setShowErrors(true);
      }
    }
  }, [activeTab, bankCountry, validate, handleSave]);

  // 스텝 모드: 이전 스텝으로 이동
  const handlePrev = useCallback(() => {
    const currentTabOrder = bankCountry === 'US' ? usdBankTabOrder : tabOrder;
    const currentIndex = currentTabOrder.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(currentTabOrder[currentIndex - 1]);
      setShowErrors(false);
    }
  }, [activeTab, bankCountry]);

  // 스텝 모드: 모든 입력 필드 초기화
  const handleClearAll = useCallback(() => {
    setProfileImage(null);
    setContactName('');
    setOrganization('');
    setOrganizationOther('');
    setBirthDate('');
    setCountry('');
    setLanguage('');
    setCertificateFile(null);
    setMinistryCountry('');
    setIsSecurityCountry(true);
    setBankName('');
    setAccountNumber('');
    setAccountHolder('');
    setBankbookFile(null);
    setUsTransferMethod('check');
    setStreetAddress('');
    setCity('');
    setState('');
    setZipCode('');
    setZelleContact('');
    setHasNoUniqueId(false);
    setUniqueId('');
    setResidentRegistrationNumberRaw('');
    setIdFile(null);
    setShowErrors(false);
    setErrors({});
  }, []);

  // 저장 성공 시 updateStatus 초기화
  useEffect(() => {
    if (updateStatus === 'saved') {
      // 일정 시간 후 상태 초기화
      const timer = setTimeout(() => {
        dispatch(clearUpdateStatus());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [updateStatus, dispatch]);
  
  return {
    // 로딩/에러 상태
    isLoading,
    isSaving,
    error: userStatus === 'failed' ? t('page.myAccount.errors.fetchUser') : undefined,
    saveError: updateError || undefined,
    userProfile,

    isDirty,

    // 탭 상태
    activeTab,
    setActiveTab,
    
    // 기본정보
    profileImage,
    setProfileImage,
    userType,
    organization,
    setOrganization,
    organizationOther,
    setOrganizationOther,
    fullName,
    contactName,
    setContactName,
    birthDate,
    setBirthDate,
    isBirthDateLocked,
    isBankInfoLocked,
    isVerificationLocked,
    showBankTabSettlementWarning,
    showVerificationTabSettlementWarning,
    phone,
    email,
    country,
    setCountry,
    language,
    setLanguage,
    
    // 사역정보
    certificateFile,
    setCertificateFile,
    ministryCountry,
    setMinistryCountry,
    isSecurityCountry,
    setIsSecurityCountry,
    
    // 은행정보
    bankCountry,
    setBankCountry,
    bankName,
    setBankName,
    accountNumber,
    setAccountNumber,
    accountHolder,
    setAccountHolder,
    bankbookFile,
    setBankbookFile,
    usTransferMethod,
    setUsTransferMethod,
    
    // 미국 주소
    streetAddress,
    setStreetAddress,
    city,
    setCity,
    state,
    setState,
    zipCode,
    setZipCode,
    zelleContact,
    setZelleContact,
    
    // 본인 확인
    hasNoUniqueId,
    setHasNoUniqueId,
    uniqueId,
    setUniqueId,
    residentRegistrationNumber,
    setResidentRegistrationNumber,
    idFile,
    setIdFile,
    
    // 기타
    verificationStatus,
    setVerificationStatus,
    errors,
    setErrors,
    showErrors,
    setShowErrors,
    isSubmitModalOpen,
    setIsSubmitModalOpen,
    isSettlementDocsCompleteModalOpen,
    setIsSettlementDocsCompleteModalOpen,

    // 액션
    validate,
    handleSave,
    handleCancel,
    handleNext,
    handlePrev,
    handleClearAll,
    refetchUser,
  };
};
